import { HttpClient, secureApiCall, ApiError } from './httpClient'
import {
  Device,
  CommandResponse,
  ChannelSchedule,
  MercuryConfig,
  DeviceConfigReadResponse,
  DeviceConfigWriteResponse,
} from './models'

/**
 * Clase para manejar las operaciones con dispositivos
 */
export class DeviceService {
  /**
   * Método para obtener todos los dispositivos
   */
  static async getDevices(): Promise<{
    devices: Device[]
    error?: string
    errorType?: string
  }> {
    return secureApiCall(
      async () => {
        const devices = await HttpClient.get<Device[]>('/devices')
        return { devices }
      },
      (error) => {
        // Manejador de errores personalizado
        let errorMessage = 'Error desconocido al conectar con el servidor'
        let errorType = 'unknown'

        if (error instanceof ApiError) {
          errorMessage = error.message;
          errorType = `http_${error.status}`;
        } else if (error instanceof TypeError && error.message.includes('fetch')) {
          errorMessage =
            'No se pudo conectar con el servidor. Verifique su conexión a internet o si el servidor está disponible.'
          errorType = 'connection'
        } else if (error instanceof SyntaxError) {
          errorMessage = 'El servidor devolvió una respuesta mal formada.'
          errorType = 'format'
        } else if (error instanceof Error) {
          errorMessage = error.message
        }

        return {
          devices: [],
          error: errorMessage,
          errorType: errorType,
        }
      },
    )
  }

  /**
   * Método para obtener un dispositivo por su ID
   */
  static async getDeviceById(deviceId: string): Promise<Device> {
    return HttpClient.get<Device>(`/devices/${deviceId}`)
  }

  /**
   * Método para crear un nuevo dispositivo
   */
  static async createDevice(deviceData: {
    name: string
    city: string
    app: string
    tagid?: string
    details?: {
      chipId?: string
      fw_name?: string
      fw?: string
      ip?: string
      rssi?: number
    }
    channels?: {
      name: string
      channel: string
      lastState?: string
    }[]
    configs?: {
      name: string
      value: any
    }[]
  }): Promise<Device> {
    return HttpClient.post<Device>('/devices', deviceData)
  }

  /**
   * Método para actualizar un dispositivo
   */
  static async updateDevice(
    deviceId: string,
    deviceData: {
      name: string
      tagid?: string
      city?: string
      app?: string
      details?: {
        chipId?: string
        fw_name?: string
        fw?: string
        ip?: string
        rssi?: number
      }
      channels?: {
        name: string
        channel: string
        lastState?: string
      }[]
      configs?: {
        name: string
        value: any
      }[]
      sentiloConfig?: {
        enabled?: boolean
        componentName?: string
        location?: string
        sensors?: {
          out0?: {
            enabled?: boolean
            sensorName?: string
            description?: string
          }
          out1?: {
            enabled?: boolean
            sensorName?: string
            description?: string
          }
        }
      }
    },
  ): Promise<Device> {
    return HttpClient.put<Device>(`/devices/${deviceId}`, deviceData)
  }

  /**
   * Método para eliminar un dispositivo
   */
  static async deleteDevice(deviceId: string): Promise<boolean> {
    await HttpClient.delete(`/devices/${deviceId}`)
    return true
  }

  /**
   * Método para enviar un comando para abrir puerta
   */
  static async openDoor(
    deviceId: string,
    channel: string,
  ): Promise<CommandResponse> {
    try {
      const data = await HttpClient.post<any>(`/cmd/${deviceId}`, {
        channel: channel,
        state: 'PULSE', // Para abrir puertas generalmente se usa un pulso
      })

      return {
        success: true,
        message: 'Comando enviado correctamente',
        requestId: data.requestId,
        status: data.status || 'done',
      }
    } catch (error) {
      console.error('Error al enviar comando:', error)
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error desconocido',
        status: error instanceof ApiError ? `http_${error.status}` : 'error'
      }
    }
  }

  /**
   * Método para actualizar el horario de un canal
   */
  static async updateChannelSchedule(
    deviceId: string,
    channel: string,
    schedule: ChannelSchedule,
  ): Promise<CommandResponse> {
    try {
      const data = await HttpClient.put<any>(
        `/devices/${deviceId}/channels/${channel}/schedule`,
        { schedule },
      )

      return {
        success: true,
        message: 'Horario actualizado correctamente',
        details: data,
      }
    } catch (error) {
      console.error('Error al actualizar schedule:', error)
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error desconocido',
        status: error instanceof ApiError ? `http_${error.status}` : 'error'
      }
    }
  }

  /**
   * Método para verificar el estado de un comando
   */
  static async checkCommandStatus(requestId: string): Promise<any> {
    return HttpClient.get(`/cmd/status/${requestId}`)
  }

  /**
   * Método para obtener el historial de un dispositivo
   */
  static async getDeviceHistory(
    deviceId: string,
    params: {
      type?: string
      action?: string
      startDate?: number
      endDate?: number
      limit?: number
      skip?: number
    } = {},
  ): Promise<{ data: any[]; meta: any }> {
    // Construir la URL con los parámetros de consulta
    let url = `/history/device/${deviceId}`
    const queryParams = new URLSearchParams()

    if (params.type) queryParams.append('type', params.type)
    if (params.action) queryParams.append('action', params.action)
    if (params.startDate)
      queryParams.append('startDate', params.startDate.toString())
    if (params.endDate) queryParams.append('endDate', params.endDate.toString())
    if (params.limit) queryParams.append('limit', params.limit.toString())
    if (params.skip) queryParams.append('skip', params.skip.toString())

    // Añadir los parámetros a la URL si hay alguno
    const queryString = queryParams.toString()
    if (queryString) {
      url += `?${queryString}`
    }

    return HttpClient.get(url)
  }

  /**
   * Método genérico para enviar comandos a un dispositivo
   * @param deviceId ID del dispositivo
   * @param channel Canal o acción a ejecutar (e.g., 'out0', 'all_data')
   * @param state Estado opcional (e.g., 'ON', 'OFF', 'PULSE')
   */
  static async sendCommand(
    deviceId: string,
    channel: string,
    state?: string,
  ): Promise<CommandResponse> {
    try {
      const body: { channel: string; state?: string; force: boolean } = {
        channel,
        force: true,
      }
      if (state !== undefined) {
        body.state = state
      }

      // Hacemos la llamada POST al endpoint de comandos
      // La API devolverá la respuesta formateada, incluyendo data/info si existen
      const responseData = await HttpClient.post<any>(`/cmd/${deviceId}`, body)

      // Asumimos que la API ya formatea una respuesta útil.
      // Solo necesitamos añadir success: true si la llamada fue bien.
      return {
        success: true,
        message: responseData.message || 'Comando enviado correctamente',
        ...responseData, // Incluir todos los campos devueltos por la API (status, requestId, state, data, info, etc.)
      }
    } catch (error) {
      console.error(`Error sending command ${channel} to ${deviceId}:`, error)
      // Intentar extraer un mensaje de error más específico si está disponible
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Error desconocido al enviar el comando'
      return {
        success: false,
        message: errorMessage,
        status: error instanceof ApiError ? `http_${error.status}` : 'error'
        // No incluir otros campos en caso de error de llamada HTTP
      }
    }
  }

  /**
   * Inicia el proceso de actualización FOTA para un dispositivo.
   * @param deviceId ID del dispositivo a actualizar.
   * @param firmwareId ID del firmware a instalar.
   */
  static async startFotaUpdate(
    deviceId: string,
    firmwareId: string,
  ): Promise<CommandResponse> {
    if (!deviceId || !firmwareId) {
      throw new Error('Se requiere el ID del dispositivo y el ID del firmware.')
    }

    try {
      // La API espera el firmwareId en el cuerpo de la petición
      const responseData = await HttpClient.post<any>(
        `/devices/${deviceId}/fota`,
        { firmwareId },
      )

      // Asumimos que la API devuelve una respuesta estándar CommandResponse
      return {
        success: true,
        message:
          responseData.message || 'Solicitud FOTA enviada correctamente.',
        ...responseData, // Incluimos cualquier otro dato devuelto por la API
      }
    } catch (error) {
      console.error(
        `Error starting FOTA for device ${deviceId} with firmware ${firmwareId}:`,
        error,
      )
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Error desconocido al iniciar FOTA'
      return {
        success: false,
        message: errorMessage,
        status: error instanceof ApiError ? `http_${error.status}` : 'error'
      }
    }
  }

  /**
   * Lee la configuración viva del dispositivo Mercury vía MQTT (_get).
   */
  static async getDeviceConfig(deviceId: string): Promise<DeviceConfigReadResponse> {
    return HttpClient.get<DeviceConfigReadResponse>(`/devices/${deviceId}/config`)
  }

  /**
   * Escribe configuración parcial en el dispositivo Mercury vía MQTT.
   * Solo envía las claves que el usuario cambió (diff).
   */
  static async updateDeviceConfig(
    deviceId: string,
    partialConfig: Partial<MercuryConfig>,
  ): Promise<DeviceConfigWriteResponse> {
    return HttpClient.post<DeviceConfigWriteResponse>(
      `/devices/${deviceId}/config`,
      partialConfig,
    )
  }

  /**
   * Registra los sensores del dispositivo en Sentilo/Dival
   * @param deviceId ID del dispositivo a registrar
   */
  static async registerSentiloSensors(deviceId: string): Promise<{
    success: boolean
    message: string
    providerId?: string
    componentName?: string
    sensors?: string[]
    error?: string
  }> {
    try {
      const responseData = await HttpClient.post<any>(
        `/devices/${deviceId}/sentilo/register`,
        {},
      )

      return {
        success: true,
        message:
          responseData.message ||
          'Sensores registrados correctamente en Sentilo.',
        providerId: responseData.providerId,
        componentName: responseData.componentName,
        sensors: responseData.sensors,
      }
    } catch (error) {
      console.error(
        `Error registering Sentilo sensors for device ${deviceId}:`,
        error,
      )
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Error desconocido al registrar sensores'
      return {
        success: false,
        message: errorMessage,
        error: errorMessage,
      }
    }
  }

  /**
   * Verifica el estado de los sensores del dispositivo en Sentilo
   * @param deviceId ID del dispositivo a verificar
   */
  static async getSentiloStatus(deviceId: string): Promise<{
    success: boolean
    sentiloEnabled?: boolean
    providerId?: string
    componentName?: string
    allSensorsRegistered?: boolean
    someSensorsRegistered?: boolean
    sensors?: {
      sensorName: string
      channel: string
      configured: boolean
      registeredInSentilo: boolean
    }[]
    totalRegisteredInProvider?: number
    sentiloError?: string
    error?: string
    message?: string
  }> {
    try {
      const responseData = await HttpClient.get<any>(
        `/devices/${deviceId}/sentilo/status`,
      )

      return {
        success: true,
        ...responseData,
      }
    } catch (error) {
      console.error(
        `Error checking Sentilo status for device ${deviceId}:`,
        error,
      )
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Error desconocido al verificar estado de Sentilo'
      return {
        success: false,
        error: errorMessage,
      }
    }
  }

  /**
   * Verifica si el dispositivo está dado de alta en Innobing
   */
  static async getInnobingStatus(deviceId: string): Promise<{
    success: boolean
    innobingEnabled?: boolean
    deviceId?: string
    registered?: boolean
    accessPoint?: any
    error?: string
  }> {
    try {
      const responseData = await HttpClient.get<any>(
        `/devices/${deviceId}/innobing/status`,
      )
      return { success: true, ...responseData }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Error desconocido al verificar estado de Innobing'
      return { success: false, error: errorMessage }
    }
  }

  /**
   * Da de alta el dispositivo en Innobing como access point
   */
  static async registerInnobingDevice(deviceId: string): Promise<{
    success: boolean
    message?: string
    accessPoint?: any
    error?: string
  }> {
    try {
      const responseData = await HttpClient.post<any>(
        `/devices/${deviceId}/innobing/register`,
        {},
      )
      return { success: true, ...responseData }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Error desconocido al dar de alta en Innobing'
      return { success: false, error: errorMessage }
    }
  }
}
